import { useEffect, useState } from "react";
import { m, AnimatePresence } from "framer-motion";
import { Menu, X, Star, Phone } from "lucide-react";
import { useLocation } from "react-router-dom";
import { BRAND, LOCATIONS } from "../data/site";
import { useBookingMode } from "../hooks/useBookingMode";

const NAV = [
  { label: "The Shop", href: "#manifesto" },
  { label: "Services", href: "#services" },
  { label: "Our Work", href: "#work" },
  { label: "Reviews", href: "#reviews" },
  { label: "Locations", href: "#locations" },
];

export const Header = ({ onBook }) => {
  const [solid, setSolid] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { bookProps } = useBookingMode();
  const href = (hash) => (pathname === "/" ? hash : `/${hash}`);

  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-header"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        solid
          ? "bg-[#0a0d12]/88 backdrop-blur-xl border-b border-white/[0.07]"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-5 lg:h-20 lg:px-10">
        <a
          href={pathname === "/" ? "#top" : "/"}
          data-testid="header-logo"
          className="flex items-center gap-2.5 group"
        >
          <img
            src={BRAND.logo}
            srcSet={`${BRAND.logo} 112w, ${BRAND.logo2x} 224w`}
            sizes="56px"
            alt="The Vault Barber Co. logo"
            width="56"
            height="56"
            fetchPriority="high"
            decoding="async"
            className="h-11 w-11 object-contain lg:h-14 lg:w-14"
          />
          <span className="font-display text-[11px] leading-none tracking-[0.2em] text-[#e9e2d3] sm:text-[13px] sm:tracking-[0.22em]">
            THE VAULT
            <span className="mt-1 block text-[8px] tracking-[0.38em] text-[#c5a059] sm:text-[9px] sm:tracking-[0.42em]">
              BARBER CO.
            </span>
          </span>
        </a>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="desktop-nav">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={href(n.href)}
              data-testid={`nav-${n.href.replace("#", "")}`}
              className="font-cond text-[13px] font-semibold uppercase tracking-[0.18em] text-slate-300 transition-colors duration-300 hover:text-[#d8b46b]"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <span className="hidden items-center gap-1.5 xl:flex">
            <Star size={13} className="fill-[#c5a059] text-[#c5a059]" />
            <span className="font-cond text-[12px] tracking-[0.14em] text-slate-300">
              {BRAND.rating} · {BRAND.reviewCount}+ GOOGLE REVIEWS
            </span>
          </span>
          <a
            href={`tel:${LOCATIONS[0].tel}`}
            data-testid="header-call-link"
            aria-label="Call the Argyle shop"
            className="flex h-11 w-11 items-center justify-center border border-white/12 text-slate-300 transition-colors duration-300 hover:border-[#c5a059] hover:text-[#d8b46b]"
          >
            <Phone size={16} />
          </a>
          <button
            type="button"
            onClick={onBook}
            data-testid="header-book-button"
            className="sheen bg-[#c5a059] px-4 py-3 font-cond text-[12px] font-bold uppercase tracking-[0.14em] text-[#0a0d12] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#d8b46b] hover:shadow-[0_10px_30px_-8px_rgba(197,160,89,0.6)] sm:px-6 sm:text-[13px] sm:tracking-[0.2em]"
          >
            <span className="sheen-bar" />
            Book<span className="hidden sm:inline"> a Chair</span>
          </button>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            className="flex h-11 w-11 items-center justify-center border border-white/12 text-slate-200 lg:hidden"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <m.div
            data-testid="mobile-nav"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-white/[0.07] bg-[#0a0d12]/97 backdrop-blur-xl lg:hidden"
          >
            <div className="px-5 py-6">
              <div className="mb-5 grid grid-cols-2 gap-2">
                {LOCATIONS.map((l) => (
                  <a
                    key={l.id}
                    {...bookProps(l)}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-menu-book-${l.id}`}
                    className="bg-[#c5a059] py-3.5 text-center font-cond text-[12px] font-bold uppercase tracking-[0.16em] text-[#0a0d12]"
                  >
                    Book {l.label}
                  </a>
                ))}
              </div>
              {NAV.map((n, i) => (
                <a
                  key={n.href}
                  href={href(n.href)}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${n.href.replace("#", "")}`}
                  className="block border-b border-white/[0.06] py-4 font-cond text-xl uppercase tracking-[0.12em] text-slate-200"
                >
                  <span className="mr-3 text-[10px] text-[#c5a059]">
                    0{i + 1}
                  </span>
                  {n.label}
                </a>
              ))}
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onBook();
                }}
                data-testid="mobile-menu-book-button"
                className="mt-6 w-full border border-[#c5a059]/50 py-4 font-cond text-sm font-bold uppercase tracking-[0.22em] text-[#e2cf9e]"
              >
                Compare both shops
              </button>
              <a
                href={`tel:${LOCATIONS[0].tel}`}
                data-testid="mobile-menu-call"
                className="mt-3 flex w-full items-center justify-center gap-2 border border-white/12 py-4 font-cond text-sm font-bold uppercase tracking-[0.18em] text-slate-200"
              >
                <Phone size={15} className="text-[#c5a059]" />
                Call {LOCATIONS[0].phone}
              </a>
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </header>
  );
};
